import { Button } from "@/components/flexkit/Button";
import { useCallback, type FC } from "react";
import { TriggerTypeSelect } from "@/components/automations/TriggerTypeSelect";
import { type WorkflowEditFormTriggerData } from "@/components/automations/automation-edit-form-data";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { WorkflowEditTriggerConfiguration } from "@/components/automations/AutomationEditTriggerConfiguration";
import type { PlainWorkflow } from "@/queries/automation-queries";

type WorkflowEditTriggerItemProps = {
    workflow: PlainWorkflow;
    trigger: WorkflowEditFormTriggerData;
    onUpdate: (trigger: WorkflowEditFormTriggerData) => void;
    onRemove: () => void;
    disabled?: boolean;
};

export const WorkflowEditTriggerItem: FC<WorkflowEditTriggerItemProps> = ({
    workflow,
    trigger,
    onUpdate,
    onRemove,
    disabled = false,
}) => {
    const handleTypeChange = useCallback(
        (newType: WorkflowEditFormTriggerData["type"]) => {
            const updatedTrigger: WorkflowEditFormTriggerData = {
                ...trigger,
                type: newType,
                config: {
                    time: newType === "time" ? { cronExpression: "" } : undefined,
                    pullRequest: newType === "pullRequest" ? { events: [] } : undefined,
                },
            };
            onUpdate(updatedTrigger);
        },
        [trigger, onUpdate],
    );

    const handleConfigChange = useCallback(
        (updatedTrigger: WorkflowEditFormTriggerData) => {
            onUpdate(updatedTrigger);
        },
        [onUpdate],
    );

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-4">
                <TriggerTypeSelect value={trigger.type} onChange={handleTypeChange} disabled={disabled} />
                <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={onRemove}
                    disabled={disabled}
                    LeadingIcon={IconTrash}
                    data-testid="remove-trigger-button"
                    data-tracking-id="remove-trigger-workflow-edit-trigger-item"
                />
            </div>
            <WorkflowEditTriggerConfiguration
                workflow={workflow}
                trigger={trigger}
                disabled={disabled}
                handleConfigChange={handleConfigChange}
            />
        </div>
    );
};
